import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Carousel, CarouselItem, Row, Col } from "reactstrap";
import { getValidityInDays } from "../Utils/UtilityFunctions";

const SubscriptionCard = ({ card }) => {
  return (
    <div
      className="p-3 h-100 position-relative"
      style={{
        border: card.isAddon ? "2px solid #e5e7eb" : "2px solid #FF6B35",
        borderRadius: "10px",
        backgroundColor: card.isAddon ? "#fff" : "#FD75450D",
      }}
    >
      {/* Badge and Check Icon */}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <span
          className="badge text-white px-3 py-2"
          style={{
            fontSize: "12px",
            backgroundColor: card.isAddon ? "#8B5CF6" : "#FD7545",
            borderRadius: "8px",
          }}
        >
          {card.isAddon ? "Add-on Offer" : "Base Offer"}
        </span>
        {!card.isAddon && (
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: "20px",
              height: "20px",
              backgroundColor: "#FF6B35",
            }}
          >
            <i
              className="mdi mdi-check text-white"
              style={{ fontSize: "14px", lineHeight: 1 }}
            ></i>
          </div>
        )}
      </div>

      {/* Title */}
      <h6 className="grant-name">{card.title}</h6>

      {/* Details */}
      <div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span style={{ fontSize: "14px", color: "#6b7280" }}>Data:</span>
          <span className="fw-normal" style={{ fontSize: "14px" }}>
            {card.balance}
          </span>
        </div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span style={{ fontSize: "14px", color: "#6b7280" }}>Validity:</span>
          <span className="fw-normal" style={{ fontSize: "14px" }}>
            {card.validity}
          </span>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <span style={{ fontSize: "14px", color: "#6b7280" }}>Price:</span>
          <span
            className="fw-normal"
            style={{
              fontSize: "14px",
              color: card.isAddon ? "#7C3AED" : "#FF6B35",
            }}
          >
            -
          </span>
        </div>
      </div>
    </div>
  );
};

const SubscriptionCarousel = ({
  subscriptions,
  fetchCustomerSummaryData,
  selectedSubscriptionId,
  aggregateResources,
}) => {
  const [slideIndex, setSlideIndex] = useState(0);

  // Build grant cards from subscriptionList.subscribedOffers
  const grantCards = useMemo(() => {
    const cards = [];
    subscriptions.forEach((sub) => {
      if (sub?.subscriptionId === selectedSubscriptionId) {
        (sub.subscribedOffers || []).forEach((offer) => {
          const resourceData = (
            fetchCustomerSummaryData?.resources || []
          ).filter(
            (res) =>
              res.offerCode === offer.offerCode &&
              res.subscriptionId === sub.subscriptionId
          );

          (resourceData || [])?.forEach((res) => {
            cards.push({
              id: offer.id || `${sub.subscriptionId}-${offer.offerCode}`,
              title: res.resourceName || offer.offerName,
              balance: res?.originalBalance || "-",
              uom: res?.uom || "-",
              validity: `${getValidityInDays(
                res?.validFrom,
                res?.validTo
              )} days`,
              description: offer.offerDescription || "Available balance",
              isAddon: !!offer.addOnOffer,
            });
          });
        });
      }
    });
    return cards;
  }, [subscriptions, selectedSubscriptionId, fetchCustomerSummaryData]);

  const getItemsPerSlide = useCallback(() => {
    return window.innerWidth < 768
      ? 1
      : aggregateResources && aggregateResources?.length > 0
      ? 2
      : 3;
  }, [aggregateResources]);

  const [itemsPerSlide, setItemsPerSlide] = useState(getItemsPerSlide);

  useEffect(() => {
    const onResize = () => setItemsPerSlide(getItemsPerSlide());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [getItemsPerSlide]);

  useEffect(() => {
    setItemsPerSlide(getItemsPerSlide());
  }, [aggregateResources]);

  // Group cards into slides
  const slides = useMemo(() => {
    const out = [];
    for (let i = 0; i < grantCards.length; i += itemsPerSlide) {
      out.push(grantCards.slice(i, i + itemsPerSlide));
    }
    return out.length ? out : [[]];
  }, [grantCards, itemsPerSlide]);

  // Reset slide index when subscription changes
  useEffect(() => {
    setSlideIndex(0);
  }, [selectedSubscriptionId]);

  const next = () => {
    if (slides.length > 1) {
      setSlideIndex((s) => (s + 1) % slides.length);
    }
  };

  const prev = () => {
    if (slides.length > 1) {
      setSlideIndex((s) => (s - 1 + slides.length) % slides.length);
    }
  };

  if (grantCards.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <p>No subscriptions available</p>
      </div>
    );
  }

  return (
    <div
      className="position-relative"
      style={{ paddingLeft: "20px", paddingRight: "20px" }}
    >
      <Carousel
        activeIndex={slideIndex}
        next={next}
        previous={prev}
        interval={false}
        ride={false}
        key={selectedSubscriptionId}
        // slide={false}
      >
        {slides.map((slideCards, idx) => (
          <CarouselItem key={idx}>
            <div className="d-flex gap-2">
              {slideCards.map((card) => (
                <div
                  key={card.id}
                  style={{
                    flex: `0 0 calc((100% - ${
                      (itemsPerSlide - 1) * 12
                    }px) / ${itemsPerSlide})`,
                    minWidth: 0,
                  }}
                >
                  <SubscriptionCard card={card} />
                </div>
              ))}
            </div>
          </CarouselItem>
        ))}
      </Carousel>

      {/* Custom Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prev}
            className="position-absolute d-flex align-items-center justify-content-center"
            style={{
              left: "-35px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "36px",
              height: "36px",
              border: "1px solid #0000001A",
              borderRadius: "8px",
              background: "white",
              cursor: "pointer",
              padding: "0",
              zIndex: 10,
            }}
            aria-label="Previous"
          >
            <i
              className="mdi mdi-chevron-left"
              style={{
                fontSize: "24px",
                color: "#0A0A0A",
                lineHeight: 1,
              }}
            ></i>
          </button>

          <button
            onClick={next}
            className="position-absolute d-flex align-items-center justify-content-center"
            style={{
              right: "-35px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "36px",
              height: "36px",
              border: "1px solid #0000001A",
              borderRadius: "8px",
              background: "white",
              cursor: "pointer",
              padding: "0",
              zIndex: 10,
            }}
            aria-label="Next"
          >
            <i
              className="mdi mdi-chevron-right"
              style={{
                fontSize: "24px",
                color: "#0A0A0A",
                lineHeight: 1,
              }}
            ></i>
          </button>
        </>
      )}

      {/* Instruction text */}
      <div className="mt-4 text-muted" style={{ fontSize: "0.875rem" }}>
        Select a subscription to view its grants and usage details below
      </div>
    </div>
  );
};

export default SubscriptionCarousel;
